import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import './style.css';
import LeadStore from '../../store/LeadStore';
import { isEmpty } from '../../utils/helper';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const EditBuildingDesignDetails = observer(({ leadId, details, handleEditDetails }) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    id: details?.clientDesignDetailId ?? null,
    leadId,
    orientation: details?.orientation,
    buildingTypology: details?.buildingTypology,
    slabAreaLimitations: details?.slabAreaLimitations,
    occupancyType: details?.occupancyType,
    occupants: details?.occupants,
    occupantsAgeGroup: details?.occupantsAgeGroup,
    occupantsOccupations: details?.occupantsOccupations,
    occupantsHobbies: details?.occupantsHobbies,
    vastuCompliance: details?.vastuCompliance,
    vastuRequirements: details?.vastuRequirements,
    createdBy: details?.CreatedBy,
    createdDate: details?.createdDate,
    updatedBy: details?.updatedBy,
    updatedDate: details?.updatedDate,
  });

  const [errors, setErrors] = useState({
    occupants: false,
    occupantsAgeGroup: false,
    occupantsOccupations: false,
    occupantsHobbies: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    occupants: '',
    occupantsAgeGroup: '',
    occupantsOccupations: '',
    occupantsHobbies: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Perform validation
    setErrors({ ...errors, [name]: isEmpty(value) });
  };

  const handleUpdateClientDesignDetailSubmit = (e) => {
    e.preventDefault();

    const updatedErrors = {
      occupants: formData.occupancyType === 'Personal Residence' && isEmpty(formData.occupants),
      occupantsAgeGroup: formData.occupancyType === 'Personal Residence' && isEmpty(formData.occupantsAgeGroup),
      occupantsOccupations: formData.occupancyType === 'Personal Residence' && isEmpty(formData.occupantsOccupations),
      occupantsHobbies: formData.occupancyType === 'Personal Residence' && isEmpty(formData.occupantsHobbies),
    };
    setErrors(updatedErrors);

    if (!Object.values(updatedErrors).some((error) => error === true)) {
      const payload = { ...formData };
      payload.vastuCompliance = payload.vastuCompliance === 'Yes';
      isEmpty(formData.id) ? LeadStore.createClientDesignDetail(payload) : LeadStore.updateClientDesignDetail(payload);
    }
  };

  useEffect(() => {
    LeadStore.updateClientDesignDetailStatus && handleEditDetails();
  }, [LeadStore.updateClientDesignDetailStatus]);

  const isContactNumberError = (value, name) => {
    if (isEmpty(value)) {
      return true;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const isNumberError = (value, name) => {
    if (isEmpty(value)) {
      return false;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const orientationList = [
    { id: 1, name: 'North-Facing' },
    { id: 2, name: 'East-Facing' },
    { id: 3, name: 'West-Facing' },
    { id: 4, name: 'South-Facing' },
    { id: 5, name: 'North-East' },
    { id: 6, name: 'North-West' },
    { id: 7, name: 'South-East' },
    { id: 8, name: 'South-West' },
  ];

  const buildingTypologyList = [
    { id: 1, name: 'Residence' },
    { id: 2, name: 'Commercial' },
    { id: 3, name: 'Mixed Use' },
  ];

  const occupancyTypeList = [
    { id: 1, name: 'Personal Residence' },
    { id: 2, name: 'Rental' },
    { id: 3, name: 'Both' },
  ];

  const vastuComplianceList = [
    { id: 1, name: 'Yes' },
    { id: 2, name: 'No' },
  ];

  return (
    <Paper sx={{ px: '30px', py: '20px', pb: '40px' }}>
      <Box
        id="client-info-form"
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdateClientDesignDetailSubmit(event);
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <FormControl variant="standard" error={Boolean(errors.orientation)}>
                    <InputLabel id="orientationLabel" shrink={true}>
                      Orientation
                    </InputLabel>
                    <Select
                      labelId="orientationLabel"
                      id="orientation"
                      name="orientation"
                      value={formData.orientation ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, ['orientation']: value });
                        setErrors({
                          ...errors,
                          ['orientation']: isEmpty(value),
                        });
                      }}
                      label="Orientation"
                      MenuProps={MenuProps}
                    >
                      {orientationList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" error={Boolean(errors.buildingTypology)}>
                    <InputLabel id="buildingTypologyLabel" shrink={true}>
                      Building Typology
                    </InputLabel>
                    <Select
                      labelId="buildingTypologyLabel"
                      id="buildingTypology"
                      name="buildingTypology"
                      value={formData.buildingTypology ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['buildingTypology']: value,
                        });
                        setErrors({
                          ...errors,
                          ['buildingTypology']: isEmpty(value),
                        });
                      }}
                      label="Building Typology"
                      MenuProps={MenuProps}
                    >
                      {buildingTypologyList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Slab area limitations, if any"
                    id="slabAreaLimitations"
                    size="small"
                    variant="standard"
                    name="slabAreaLimitations"
                    value={formData.slabAreaLimitations ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <FormControl variant="standard" error={Boolean(errors.occupancyType)}>
                    <InputLabel id="occupancyTypeLabel" shrink={true}>
                      Occupancy Type
                    </InputLabel>
                    <Select
                      labelId="occupancyTypeLabel"
                      id="occupancyType"
                      name="occupancyType"
                      value={formData.occupancyType ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['occupancyType']: value,
                          ['occupants']: value !== 'Personal Residence' ? null : formData.occupants,
                          ['occupantsAgeGroup']: value !== 'Personal Residence' ? null : formData.occupantsAgeGroup,
                          ['occupantsOccupations']:
                            value !== 'Personal Residence' ? null : formData.occupantsOccupations,
                          ['occupantsHobbies']: value !== 'Personal Residence' ? null : formData.occupantsHobbies,
                        });
                        setErrors({
                          ...errors,
                          ['occupancyType']: isEmpty(value),
                        });
                      }}
                      label="Occupancy Type"
                      MenuProps={MenuProps}
                    >
                      {occupancyTypeList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.occupancyType === 'Personal Residence' && (
                    <TextField
                      label="Occupants"
                      id="occupants"
                      size="small"
                      variant="standard"
                      required={true}
                      name="occupants"
                      multiline
                      rows={1}
                      value={formData.occupants ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.occupants)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.occupancyType === 'Personal Residence' && (
                    <TextField
                      label="Occupants Age Group"
                      id="occupantsAgeGroup"
                      size="small"
                      variant="standard"
                      required={true}
                      name="occupantsAgeGroup"
                      multiline
                      rows={1}
                      value={formData.occupantsAgeGroup ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.occupantsAgeGroup)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.occupancyType === 'Personal Residence' && (
                    <TextField
                      label="Occupants Occupations"
                      id="occupantsOccupations"
                      size="small"
                      variant="standard"
                      required={true}
                      name="occupantsOccupations"
                      multiline
                      rows={1}
                      value={formData.occupantsOccupations ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.occupantsOccupations)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  {formData.occupancyType === 'Personal Residence' && (
                    <TextField
                      label="Occupants Hobbies"
                      id="occupantsHobbies"
                      size="small"
                      variant="standard"
                      required={true}
                      name="occupantsHobbies"
                      multiline
                      rows={1}
                      value={formData.occupantsHobbies ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.occupantsHobbies)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  <FormControl variant="standard" error={Boolean(errors.vastuCompliance)}>
                    <InputLabel id="vastuComplianceLabel" shrink={true}>
                      Vastu Compliance
                    </InputLabel>
                    <Select
                      labelId="vastuComplianceLabel"
                      id="vastuCompliance"
                      name="vastuCompliance"
                      value={formData.vastuCompliance ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          ['vastuCompliance']: value,
                          ['vastuRequirements']: value !== 'Yes' ? null : formData.vastuRequirements,
                        });
                        setErrors({
                          ...errors,
                          ['vastuCompliance']: isEmpty(value),
                        });
                      }}
                      label="Vastu Compliance"
                      MenuProps={MenuProps}
                    >
                      {vastuComplianceList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.vastuCompliance === 'Yes' && (
                    <TextField
                      label="Vastu Requirements"
                      id="vastuRequirements"
                      size="small"
                      variant="standard"
                      required={true}
                      name="vastuRequirements"
                      multiline
                      rows={1}
                      value={formData.vastuRequirements ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.vastuRequirements)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack direction={'row'} sx={{ ml: 'auto' }} alignSelf={isSmallScreen ? '' : 'end'} spacing={2}>
            <Button variant="outlined" color="inherit" fullWidth size="small" onClick={() => handleEditDetails(false)}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" fullWidth color="primary" size="small">
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
});
